/** Values a provider must snapshot to prove its callbacks and policy unchanged. */

import { Invalidation, type Option, RowDataChanged, TimepointsChanged, type Unsubscribe } from "@tento-lona/sheets";
import { DomFlags } from "@tento-ui/dom-flags";

/**
 * One value of a read identity. `undefined` is a member on purpose: an
 * identity lists reads such as `prefs?.peek().dateFormat`, whose absent
 * receiver yields `undefined`, and the snapshot compares what was read.
 */
export type CalendarReadValue = object | string | number | boolean | null | undefined;

type RevisionSource = { subscribe(callback: (event: Option<Invalidation>) => void): Unsubscribe };

/** Days since the epoch, end exclusive. */
type DayRange = { readonly start: number; readonly end: number };

const MS_PER_DAY = 86_400_000;

/**
 * One counter per raw invalidation source, before transaction deduplication.
 *
 * It also keeps which days each recent change can have touched, for the
 * dated reuse (`calendar-dated-read-reuse`): a completed read of
 * some days stays reusable across changes that cannot touch them.
 * - `TimepointsChanged` touches its time range, one day of zone slack each side.
 * - A ranged `RowDataChanged` (an install of dates from `a`) may touch every
 *   day from `a - 1` on: a row's cell for a date can carry an event that runs
 *   on past it, never one that started before it.
 * - A first coverage (`coverage.*`: no cell changed, nothing repaints) touches none.
 * - Anything else (a whole-row change, `ViewportsInvalidated`, a column, theme
 *   or structure change, an event-less advance) touches every day.
 */
export class CalendarReadRevision {
  static readonly #sources = new WeakMap<RevisionSource, CalendarReadRevision>();
  /** How many dated changes are kept; a read older than the oldest kept is not reusable by date. */
  static readonly DATED_LOG = 256;
  #revision = 0;
  /** The revision of the last change no day range bounds. */
  #undatedAt = 0;
  /** The newest revision dropped from the dated log. */
  #floor = 0;
  /** Dated changes, oldest first: the revision each produced and the days it may touch. */
  #dated: { readonly revision: number; readonly days: DayRange }[] = [];

  static for(source: RevisionSource): CalendarReadRevision {
    const existing = this.#sources.get(source);
    if (existing) return existing;
    const clock = new CalendarReadRevision();
    // The listener belongs to the source's lifetime and captures only the
    // counter. Neither this weak registry nor the listener retains a component.
    source.subscribe((event) => clock.note(event));
    this.#sources.set(source, clock);
    return clock;
  }

  /** Whether reads are reusable across changes that cannot touch their days (`DomFlags`; on by default since the operator's flip of 2026-10-12). */
  static datedReuse(): boolean {
    return DomFlags.read("calendar-dated-read-reuse", true);
  }

  get value(): number { return this.#revision; }
  /** Something changed, nobody says where. */
  advance(): void { this.note(null); }

  /** `event` happened: advance, and remember which days it can have touched. */
  note(event: Option<Invalidation>): void {
    this.#revision++;
    const days = CalendarReadRevision.#touched(event);
    if (days == null) {
      this.#undatedAt = this.#revision;
      return;
    }
    if (days.end <= days.start) return;
    this.#dated.push({ revision: this.#revision, days });
    if (this.#dated.length > CalendarReadRevision.DATED_LOG) this.#floor = this.#dated.shift()?.revision ?? this.#floor;
  }

  /** Whether nothing after `revision` can have touched `days`: every later change is dated and misses them. */
  unchangedFor(revision: number, days: { readonly start: number; readonly end: number }): boolean {
    if (revision === this.#revision) return true;
    if (revision < this.#undatedAt || revision < this.#floor) return false;
    for (const change of this.#dated) {
      if (change.revision > revision && change.days.start < days.end && change.days.end > days.start) return false;
    }
    return true;
  }

  /** The days `event` can have touched (empty: none), or null for every day. */
  static #touched(event: Option<Invalidation>): Option<DayRange> {
    if (event == null) return null;
    if (event instanceof TimepointsChanged) {
      return {
        start: Math.floor(event.range.start / MS_PER_DAY) - 1,
        end: Math.floor((event.range.end - 1) / MS_PER_DAY) + 2,
      };
    }
    if (event instanceof RowDataChanged) {
      const range = event.range;
      return range == null ? null : { start: range.start.dse - 1, end: Number.POSITIVE_INFINITY };
    }
    if (Invalidation.matches(event, "coverage")) return { start: 0, end: 0 };
    return null;
  }
}

export type CalendarReadTicket = {
  readonly clock: CalendarReadRevision;
  readonly revision: number;
  readonly inputs: readonly CalendarReadValue[];
};

/**
 * A component may reuse only its own completed read at the current revision,
 * or, given the days it shows (dated reuse), at a revision no later change
 * can have touched those days in (`CalendarReadRevision.unchangedFor`).
 */
export class CompletedCalendarRead {
  #current: Option<CalendarReadTicket> = null;
  #completed = false;
  /** The newest revision the completed read is known current at. */
  #validAt = 0;

  canReuse(clock: CalendarReadRevision, inputs: readonly CalendarReadValue[]): boolean {
    return this.#sameRead(clock, inputs) && this.#current?.revision === clock.value;
  }

  /**
   * `canReuse`, or the completed read of `days` with no change since it that
   * can touch them. A reuse by date moves the read's known-current revision to
   * now; the ticket object stays, so paint keys still match.
   */
  canReuseDays(
    clock: CalendarReadRevision,
    inputs: readonly CalendarReadValue[],
    days: { readonly start: number; readonly end: number },
  ): boolean {
    if (!this.#sameRead(clock, inputs)) return false;
    if (this.#current?.revision === clock.value) return true;
    if (!clock.unchangedFor(this.#validAt, days)) return false;
    this.#validAt = clock.value;
    return true;
  }

  /** A completed read on `clock` with identical `inputs`. */
  #sameRead(clock: CalendarReadRevision, inputs: readonly CalendarReadValue[]): boolean {
    const read = this.#current;
    return this.#completed && read != null && read.clock === clock && read.inputs.length === inputs.length &&
      read.inputs.every((value, index) => Object.is(value, inputs[index]));
  }

  begin(clock: CalendarReadRevision, inputs: readonly CalendarReadValue[]): CalendarReadTicket {
    const read = { clock, revision: clock.value, inputs: [...inputs] };
    this.#current = read;
    this.#completed = false;
    this.#validAt = read.revision;
    return read;
  }

  complete(read: CalendarReadTicket): void {
    if (this.#current === read) this.#completed = true;
  }

  fail(read: CalendarReadTicket): void {
    if (this.#current === read) this.clear();
  }

  clear(): void {
    this.#current = null;
    this.#completed = false;
  }
}
